import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import { OrderSummaryItem } from "@/components/cart/OrderSummary/OrderSummaryItem"
import {
  maxAppReached,
  stopedByPreviousDiscount,
  cartPage,
  API_SUCCESS_STATUS,
  discountMatchesCart,
} from "@/constants/index"
import { getTranslations } from "@/components/checkout/localization"
import { useTranslation } from "next-i18next"
import { calculateCartData, sanitizeTextForAnalytics } from "@/utils/helper"
import OrderReview from "@/components/checkout/OrderReview"
import ExpressCheckout from "@/components/Shared/ExpressCheckout"

import Link from "next/link"
import { useRouter } from "next/router"
import { selectCartState } from "@/store/features/cartSlice"
import Collapsible from "@/components/core/Collapsible"
import {
  addAnalyticsEditCart,
  addAnalyticsAccordianClickPromocode,
  continueCheckoutEventInfo,
} from "@/components/cart/analytics"
import {
  checkoutState,
  setPaymentTechnicalFailedAction,
  setPaymentDisableBtnAction,
  setPlaceOrderCountsAction,
} from "@/store/features/checkoutSlice"
import Modal from "@/components/core/Modal/Modal"
import htmlParser from "html-react-parser"
import { addAnalyticsForErrorMessages } from "@/components/checkout/PaymentMethod/analytics"

import TooltipIcon from "@/public/icons/small-tooltip.svg"

const OrderSummary = props => {
  const {
    subtotal,
    totalPromotions,
    estimatedTax,
    estimatedTotal,
    promoCodes = [],
    i18n = {},
    checkoutMode = "",
    shippingDone = "",
    status,
    toastError,
    dispatchCallback,
    cartItems = [],
    disableCheckout,
    checkoutUrl,
    currencySign,
    oldPromoCodes,
    oldDiscountCodes,
    shippingAllTotal = [],
    legalLinks = {},
    technicalErrorMessage = {},
    placeOrderTechnicalErrorMsg = "",
    placeOrderBtnDisableMsg = "",
  } = props || {}

  const { cart: activeCart = {} } = useSelector(selectCartState)

  const {
    placeOrderErrorTitleMsg = "",
    placeOrderErrorMsg = "",
    placeOrderErrorContactMsg = "",
    placeOrderErrorHelpMsg = "",
  } = technicalErrorMessage
  const {
    isPaymentTechnicalErrorFailed = false,
    isPaymentDisableAction = false,
  } = useSelector(checkoutState)

  const { key: cartKey = "" } = activeCart
  const isLoading = status === "loading"
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [technicalErrorModal, setTechnicalErrorModal] = useState(false)
  const [disablePaymentBtn, setDisablePaymentBtn] = useState(false)
  const { t } = useTranslation("common")
  const staticText = getTranslations(t)
  const dispatch = useDispatch()
  const router = useRouter()
  const { genericError = "" } = staticText
  const paymentButtonDisable = () => {
    addAnalyticsForErrorMessages(
      placeOrderBtnDisableMsg
        ? placeOrderBtnDisableMsg.replace(/<[^>]+>|&nbsp;/g, " ").trim()
        : genericError,
      "n/a",
      true,
      "close",
      ":let us help you"
    )

    dispatch(setPlaceOrderCountsAction(0))
    dispatch(setPaymentTechnicalFailedAction(false))
    dispatch(setPaymentDisableBtnAction(false))
    setDisablePaymentBtn(true)
  }
  useEffect(() => {
    if (isPaymentTechnicalErrorFailed && placeOrderTechnicalErrorMsg) {
      setIsModalOpen(true)
      addAnalyticsForErrorMessages(
        placeOrderTechnicalErrorMsg,
        "failure",
        isModalOpen
      )
    }
  }, [isPaymentTechnicalErrorFailed, placeOrderTechnicalErrorMsg])

  useEffect(() => {
    if (isPaymentDisableAction) {
      setTechnicalErrorModal(true)
    }
  }, [isPaymentDisableAction])

  const handleModalClose = () => {
    addAnalyticsForErrorMessages(
      placeOrderTechnicalErrorMsg || genericError,
      "n/a",
      true,
      "close",
      ":let us help you"
    )
    setIsModalOpen(false)
  }

  const handletechnicalErrorPopupClose = () => {
    setIsModalOpen(true)
  }
  const { shippingAddress: { streetName = "" } = {} } = activeCart

  const { freeShipping = "" } = staticText

  const [promoCode, setPromoCode] = useState("")
  const [promoError, setPromoError] = useState("")
  const [oldPromoCode, setOldPromoCode] = useState([])
  const [oldDiscountCode, setOldDiscountCode] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const handalScroll = () => {
    document
      .querySelector(".order-summary-mobile-sticky")
      .classList.remove("is-sticky-load")
  }
  useEffect(() => {
    if (!checkoutMode) {
      const browserWidth = document.body.clientWidth
      if (browserWidth <= 1023) {
        window.addEventListener("scroll", handalScroll)
        const observer = new IntersectionObserver(
          function (entries) {
            const mobileSticky = document.querySelector(
              ".order-summary-mobile-sticky"
            )
            if (mobileSticky) {
              if (entries[0].intersectionRatio === 0) {
                mobileSticky.classList?.add("isSticky")
              } else if (entries[0].intersectionRatio === 1) {
                mobileSticky.classList?.remove("isSticky")
              }
            }
          },
          { threshold: [0, 1] }
        )
        observer.observe(document.querySelector(".order-summary"))
        return () => window.removeEventListener("scroll", handalScroll)
      }
    }
  }, [estimatedTotal])

  useEffect(() => {
    if (promoError) document.getElementById("kf-cart-promo-error")?.focus()
  }, [promoError])

  useEffect(() => {
    setOldPromoCode(oldPromoCodes)
  }, [oldPromoCodes])

  useEffect(() => {
    setOldDiscountCode(oldDiscountCodes)
  }, [oldDiscountCodes])

  const handleAddDiscount = (promo = null) => {
    promo.stopPropagation()
    const code = promo.target.value || promoCode.trim()
    const isAlreadyApplied = promoCodesData.some(
      item => item.code.toLowerCase() === code.toLowerCase()
    )
    if (isAlreadyApplied) {
      setPromoError(i18n?.alreadyAppliedErr ?? "")
      setPromoCode("")
      return
    }
    const payload = {
      actions: [
        {
          code: code,
          action: "addDiscountCode",
        },
      ],
    }
    code
      ? dispatchCallback("discount", payload)
          .unwrap()
          .then(function (rsp) {
            handleDiscountResponse(
              rsp,
              oldPromoCode,
              oldDiscountCode,
              API_SUCCESS_STATUS
            )
            setPromoCode("")
          })
          .catch(er => {
            updatePromoAnalytics(
              i18n?.invalidPromoErr ?? "",
              undefined,
              "",
              "apply"
            )
            setPromoError(i18n?.invalidPromoErr ?? "")
          })
      : setPromoError(i18n?.noPromoErr ?? "")
    !code &&
      updatePromoAnalytics(i18n?.noPromoErr ?? "", undefined, "", "apply")
  }

  const handleRemoveDiscount = (Id, code) => {
    const payload = {
      actions: [
        {
          action: "removeDiscountCode",
          discountCode: {
            typeId: "discount-code",
            id: Id,
          },
        },
      ],
    }
    if (Id) {
      dispatchCallback("discount", payload)
        .unwrap()
        .then(async resp => {
          await updatePromoAnalytics("success", resp, code, "remove")
          dispatchCallback("taxCalculation")
        })
        .catch(async () => {
          await updatePromoAnalytics(
            "failed to remove discount code",
            undefined,
            code,
            "remove"
          )
          toastError(i18n.removeDiscountErrorMsg)
        })
    }
  }

  const handleDiscountResponse = (
    payload,
    oldPromoCodePassed,
    oldDiscountCodePassed,
    status
  ) => {
    const appliedPromoCode = promoCode?.trim().toLowerCase()
    if (status === "succeeded" && payload) {
      // Check if previous discounts are removed by current discount
      let previousDiscountError = true
      oldDiscountCodePassed.forEach(oldCode => {
        const newCode = payload.discountCodes?.find(
          code =>
            code.discountCode?.obj?.code === oldCode.discountCode?.obj?.code
        )
        if (
          previousDiscountError &&
          oldCode.state === "MatchesCart" &&
          oldPromoCodePassed?.find(
            promo => promo.code === oldCode.discountCode?.obj?.code
          ) &&
          newCode?.state === "ApplicationStoppedByPreviousDiscount"
        ) {
          previousDiscountError = false
        }
      })
      if (!previousDiscountError) {
        setPromoError(i18n?.previousDiscountErr ?? "")
        updatePromoAnalytics(
          i18n?.previousDiscountErr ?? "",
          payload,
          appliedPromoCode,
          "apply"
        )
        toastError(i18n?.previousDiscountErr ?? "")
      } else {
        const resCode =
          payload.discountCodes?.find(
            code =>
              code.discountCode?.obj?.code?.toLowerCase() === appliedPromoCode
          ) ?? {}
        // Check if applied code can be combined with existing discounts
        if (resCode.state === stopedByPreviousDiscount) {
          setPromoError(i18n?.combinePromoErr ?? "")
          updatePromoAnalytics(
            i18n?.combinePromoErr ?? "",
            payload,
            appliedPromoCode,
            "apply"
          )
        }
        // Check if applied code has reached the maximum number of times it can be applied
        else if (resCode.state === maxAppReached) {
          setPromoError(i18n?.maxAppReachedErr ?? "")
          updatePromoAnalytics(
            i18n?.maxAppReachedErr ?? "",
            payload,
            appliedPromoCode,
            "apply"
          )
        } else if (resCode.state === discountMatchesCart) {
          updatePromoAnalytics("success", payload, appliedPromoCode, "apply")
          setPromoCode("")
          setPromoError("")
        } else {
          const errorMessage = i18n?.promoNotApplicable ?? ""
          setPromoError(errorMessage)
          updatePromoAnalytics(errorMessage, payload, appliedPromoCode, "apply")
        }
      }
    }
    dispatchCallback("taxCalculation")
  }

  const updatePromoAnalytics = async (status, cartObj, promo, action) => {
    const page = window.pageObj || {}
    const eventInfo = {
      eventAction: `cart:landing page:${action} promo`,
      eventName: `cart:landing page:${action} promo`,
      eventType: "navigation",
      eventMsg:
        status === "success"
          ? action === "apply"
            ? "promocode applied successfully"
            : "promocode removed successfully"
          : status?.toLowerCase(),
      eventStatus: status === "success" ? "success" : "failure",
      internalLinkName: action,
      internalLinkPosition: "cart",
      internalLinkType: "cart:promo",
      internalLinkZoneName: "cart:landing page:order summary",
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
      promoCode: promo,
    }

    if (cartObj) {
      const { cart } = await calculateCartData(
        {
          cart: cartObj,
          i18n: {
            productDiscounts: i18n.productDiscounts || "Product Discounts",
          },
        },
        true
      )

      page.cart = cart
    }

    window.adobeDataLayer.push({
      event: "cmp:click",
      eventInfo,
      page,
    })
  }
  const handleMouseOverTooltip = type => {
    const eventText = `cart:landing page:${
      type === i18n?.oversizedFragileDelivery
        ? type.toLowerCase().replace("/", " ")
        : `estimated ${type}`
    } mouseover`

    const eventLinkName =
      type === i18n?.oversizedFragileDelivery
        ? sanitizeTextForAnalytics(i18n?.oversizedDelivery)
            ?.split("  ")
            ?.join(" ")
        : i18n?.shippingTooltip.toLowerCase()

    const eventInfo = {
      eventAction: eventText,
      eventName: eventText,
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName:
        type === "tax" ? i18n?.taxTooltip.toLowerCase() : eventLinkName,
      internalLinkPosition: "cart",
      internalLinkType: "cart:mouseover",
      internalLinkZoneName: "cart:landing page:order summary",
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
    }

    const { adobeDataLayer: { getState } = {} } = window
    const page = (getState && getState("page")) || {}
    page.cart = props?.cart

    window.adobeDataLayer.push({
      event: "cmp:click",
      eventInfo,
      page,
    })
  }
  const promoCodesData = promoCodes.filter(e => {
    if (e.type !== "product" && e.type !== "no-code" && e.id) return e
  })

  const {
    shipping = "",
    shippingTooltip = "",
    oversizedFragileDelivery = "",
    oversizedDelivery = "",
    freightShipping = "",
    freightLabel = "",
    editCart = "",
  } = i18n

  if (shippingAllTotal.length) {
    const fromIndex = shippingAllTotal.findIndex(
      el => el?.name === freightShipping || el?.name === freightLabel
    )
    const toIndex = shippingAllTotal.length - 1
    shippingAllTotal.splice(
      toIndex,
      0,
      shippingAllTotal.splice(fromIndex, 1)[0]
    )
  }

  const isCartItems = cartItems.length

  const promoCodeAccordian = () => {
    const promoCodeIsOpenTitle = !isOpen ? "expand" : "collapse"
    setIsOpen(!isOpen)
    addAnalyticsAccordianClickPromocode(promoCodeIsOpenTitle, activeCart)
  }

  return (
    <div className="row no-gutters order-summary-wrapper sticky-order">
      <div className="col-12">
        <div
          className="order-summary "
          aria-live="polite"
          aria-busy={isLoading}
          aria-relevant="text additions"
        >
          <div className="order-summary__title" role="heading" aria-level="2">
            {i18n.orderSummary}{" "}
            {checkoutMode ? (
              <Link href={cartPage} prefetch={false}>
                <p
                  tabIndex="0"
                  role="link"
                  aria-label={staticText.backToCart}
                  className="edit-cart-btn"
                  onClick={() =>
                    addAnalyticsEditCart(
                      window.location.origin.toLowerCase() + cartPage,
                      activeCart
                    )
                  }
                >
                  {editCart}
                </p>
              </Link>
            ) : null}
          </div>
          <OrderSummaryItem
            value={isCartItems ? subtotal : "-"}
            label={i18n.subTotal}
            prefix={currencySign}
          />
          {Number(totalPromotions) ? (
            <OrderSummaryItem
              label={i18n.itemDiscount}
              customCls="order-font-medium"
              value={totalPromotions > 0 ? totalPromotions * -1 : ""}
              prefix={currencySign}
            />
          ) : null}

          {isCartItems
            ? shippingAllTotal.map((item = {}, i) => {
                const { name = "", total = 0 } = item
                const isFreightShipping =
                  name === freightShipping || name === freightLabel

                const shippingName = !isFreightShipping
                  ? `${name} ${shipping}`
                  : oversizedFragileDelivery

                const totalAmount = Number(total) ? total : freeShipping
                const tooltip = !isFreightShipping
                  ? shippingTooltip
                  : oversizedDelivery

                return (
                  <React.Fragment key={i}>
                    <OrderSummaryItem
                      value={totalAmount}
                      label={shippingName}
                      icon={!checkoutMode ? TooltipIcon?.src : null}
                      alt={`${i18n.shippingInfoAria} ${tooltip}`}
                      tooltip={tooltip}
                      onMouseOver={handleMouseOverTooltip}
                      type={
                        shippingName === oversizedFragileDelivery
                          ? oversizedFragileDelivery
                          : "shipping"
                      }
                      prefix={currencySign}
                    />
                  </React.Fragment>
                )
              })
            : null}
          <OrderSummaryItem
            value={
              parseFloat(estimatedTax) || streetName
                ? estimatedTax
                : checkoutMode
                ? i18n.taxCalculatedInNextStep
                : i18n.calculatedInCheckout
            }
            label={streetName ? i18n.tax : i18n.estimatedTax}
            prefix={currencySign}
          />
          <hr
            role="presentation"
            aria-hidden="true"
            className="order-summary__separator cart_custom_margin_hr"
          />

          <div
            className={`order-summary-mobile-sticky   ${
              !checkoutMode ? "is-sticky-load" : ""
            }`}
          >
            <OrderSummaryItem
              label={shippingDone ? i18n.total : i18n.estimatedTotal}
              value={isCartItems ? estimatedTotal : "-"}
              lineBreak={false}
              prefix={currencySign}
              valueSize="large"
              customCls="estimated-total"
            />
            <div className="checkout-button-group">
              {!checkoutMode && (
                /* TODO: Enable this button when checkout v2 is ready and clean up cookie logic button code*/
                //   <Button
                //     ariaLabel={i18n.continueCheckoutAria(
                //       cartItems?.length,
                //       `${currencySign}${estimatedTotal}`
                //     )}
                //     onClick={() =>
                //       continueCheckoutEventInfo(checkoutUrl, activeCart)
                //     }
                //     className="gbh-data-layer checkout-btn-group"
                //     label={i18n.checkout}
                //     flexible
                //     type="black"
                //     to={checkoutUrl}
                //     disabled={disableCheckout}
                //  />
                <Button
                  ariaLabel={i18n.continueCheckoutAria(
                    cartItems?.length,
                    `${currencySign}${estimatedTotal}`
                  )}
                  onClick={e => {
                    e.preventDefault()
                    continueCheckoutEventInfo(checkoutUrl, activeCart)
                    // Check if checkoutv2=yes cookie exists
                    if (document.cookie.includes("checkoutv2=yes")) {
                      // Force full page reload for v2
                      window.location.href = checkoutUrl + "?mode=v2"
                    } else {
                      // Normal SPA navigation
                      router.push(checkoutUrl)
                    }
                  }}
                  className="gbh-data-layer checkout-btn-group"
                  label={i18n.checkout}
                  flexible
                  type="black"
                  to={checkoutUrl}
                  disabled={disableCheckout}
                />
              )}
              {!checkoutMode ? <ExpressCheckout orderSummary /> : null}
            </div>
            {checkoutMode && isPaymentTechnicalErrorFailed && isModalOpen ? (
              <div className="payment-fail-modal">
                <Modal
                  showModal={isPaymentTechnicalErrorFailed && isModalOpen}
                  onModalClose={handleModalClose}
                  labelledBy="error-message"
                  hideCloseIcon={false}
                  i18n={i18n}
                >
                  <p className="error-title">{placeOrderErrorTitleMsg}</p>
                  <p className="fail-message">
                    {placeOrderTechnicalErrorMsg || genericError}
                  </p>
                  <OrderReview
                    i18n={i18n}
                    orderSummary
                    cart={activeCart}
                    placeOrderClick={handleModalClose}
                    technicalErrorPopup={handletechnicalErrorPopupClose}
                    errorMessage={placeOrderTechnicalErrorMsg || genericError}
                    isModalOpen={true}
                  />
                </Modal>
              </div>
            ) : null}
            {checkoutMode && technicalErrorModal && !disablePaymentBtn ? (
              <div className="payment-fail-modal">
                <Modal
                  showModal={technicalErrorModal && !disablePaymentBtn}
                  onModalClose={paymentButtonDisable}
                  labelledBy="error-message"
                  hideCloseIcon={false}
                  i18n={i18n}
                >
                  {technicalErrorMessage ? (
                    <>
                      <p className="error-title">{placeOrderErrorTitleMsg}</p>
                      <p className="fail-message">{placeOrderErrorMsg}</p>
                      {cartKey ? (
                        <p className="user-cart-key">
                          {i18n.yourSharedCartID(cartKey)}
                        </p>
                      ) : null}
                      {placeOrderErrorContactMsg ? (
                        <p
                          className="payment-error-contact"
                          onClick={e => {
                            addAnalyticsForErrorMessages(
                              "n/a",
                              "n/a",
                              true,
                              e?.target?.text?.toLowerCase() ?? "n/a"
                            )
                          }}
                        >
                          {htmlParser(placeOrderErrorContactMsg)}
                        </p>
                      ) : null}
                      {placeOrderErrorHelpMsg ? (
                        <p
                          className="payment-help-msg"
                          onClick={e => {
                            addAnalyticsForErrorMessages(
                              "n/a",
                              "n/a",
                              true,
                              e?.target?.text?.toLowerCase() ?? "n/a"
                            )
                          }}
                        >
                          {htmlParser(placeOrderErrorHelpMsg)}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    genericError
                  )}
                </Modal>
              </div>
            ) : null}
            {checkoutMode ? (
              <OrderReview
                i18n={i18n}
                orderSummary
                cart={activeCart}
                legalLinks={legalLinks}
                placeOrderBtnDisableMsg={placeOrderBtnDisableMsg}
                placeOrderClick={handleModalClose}
                technicalErrorPopup={handletechnicalErrorPopupClose}
                errorMessage={placeOrderTechnicalErrorMsg || genericError}
              />
            ) : null}
          </div>
        </div>

        <div className="cart-accordian" onClick={promoCodeAccordian}>
          <Collapsible
            title={i18n.promocode}
            isOpenAccordian={promoCodesData.length}
          >
            <div className="cart-accordion-section">
              {promoCodesData.length ? (
                <div className="cart-promo-code-section">
                  {promoCodesData?.map((item, i) => (
                    <div className="promo-code-list" key={i}>
                      <div className="cart-promo-code">
                        <span className="active-promo-name">{item.code}</span>
                        <p className="promo-right-section">
                          <span className="active-promo-price">
                            {item.type !== "shipping" ? (
                              <OrderSummaryItem
                                value={Number(item.discount * -1)}
                                prefix={currencySign}
                              />
                            ) : (
                              ""
                            )}
                          </span>
                          <span
                            tabIndex={0}
                            className="order-summary__close"
                            onClick={() => {
                              handleRemoveDiscount(item.id, item.code)
                            }}
                          >
                            {i18n.remove}
                          </span>
                        </p>
                      </div>
                      {/* <p className="cart-promo-description">
                            10% off qualifying KOHLER products
                          </p> */}
                    </div>
                  ))}
                </div>
              ) : null}

              <div
                className="order-summary__input-group"
                onClick={e => e.stopPropagation()}
              >
                <Input
                  id="promo-code-input"
                  customClass={`order-summary__input ${
                    promoError ? "--error" : ""
                  }`}
                  className="promo-code-input"
                  label={i18n.enterPromo}
                  type="text"
                  pattern="[a-zA-Z0-9]+"
                  placeholder={i18n.enterPromo}
                  value={promoCode}
                  onChange={e => setPromoCode(e.target.value)}
                  onKeyPress={e => e.key === "Enter" && handleAddDiscount(e)}
                  onFocus={() => {
                    setPromoError("")
                  }}
                />

                <Button
                  role="button"
                  tabIndex="0"
                  label={i18n.apply}
                  size="small"
                  type="ghost"
                  disabled={status === "loading"}
                  onClick={handleAddDiscount}
                />
              </div>
              {promoError && (
                <div
                  tabIndex="0"
                  role="alert"
                  id="kf-cart-promo-error"
                  className="order-summary__promo-error"
                >
                  {promoError}
                </div>
              )}
            </div>
          </Collapsible>
        </div>
      </div>
    </div>
  )
}

export default OrderSummary
